import {
  Box,
  Button,
  Grid,
  Modal,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Skeleton,
  Autocomplete,
  TextField,
  gridClasses,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { scheduleFollowUpReminder } from "../../utils/reminder";
import EditIcon from "@mui/icons-material/Edit";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DataGrid } from "@mui/x-data-grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useOrganization } from "../../utils/OrganizationContext";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ThemedDatePicker from "../../utils/ThemedDatePicker";
import ThemedTimePicker from "../../utils/ThemedTimePicker";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";
const WA = "#1B6B41";

const viewTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: "#084767", contrastText: "#FFFFFF" },
    text: { primary: INK, secondary: "#4A5A6E" },
    divider: "#EDF1F5",
  },
  shape: { borderRadius: 10 },
  typography: { fontFamily: FONT, button: { textTransform: "none", fontWeight: 600 } },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, fontFamily: FONT, fontWeight: 600 },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        outlined: { borderColor: FIELD_EDGE, background: "#FFFFFF", color: INK, "&:hover": { background: "#F4F7FA", borderColor: "#B4C6DA" } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          background: "#FFFFFF",
          fontFamily: FONT,
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: { styleOverrides: { root: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, minHeight: 36 } } },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
        groupLabel: { fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: MUTED, lineHeight: "30px" },
      },
    },
    MuiBackdrop: { styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { background: "rgba(15,27,42,0.46)" } } } },
    MuiSkeleton: { styleOverrides: { root: { background: "#EDF1F5" } } },
  },
});

const cardStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: "#FFFFFF",
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  padding: "18px 20px 20px",
  margin: "0 0 16px",
  boxShadow: "none",
};
const h2Style = {
  margin: 0,
  fontFamily: FONT,
  fontSize: 20,
  fontWeight: 800,
  letterSpacing: "-0.02em",
  color: INK,
};
const sectionLabel = {
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: MUTED,
};
const hrStyle = { border: 0, borderTop: "1px solid #EDF1F5", margin: "14px 0" };
const factLabel = {
  display: "block",
  fontFamily: FONT,
  color: MUTED,
  fontWeight: 600,
  fontSize: "11px",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  marginBottom: 3,
};
const factValue = {
  fontFamily: FONT,
  color: INK,
  fontWeight: 600,
  fontSize: "14px",
  wordBreak: "break-word",
};
const labelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};
const waBtnSx = {
  background: "#E9F7EE",
  color: WA,
  border: "1px solid #C4E8D2",
  height: 30,
  px: 0.75,
  borderRadius: "9px",
  "&:hover": { backgroundColor: "#DCF1E4" },
  "&:active": { backgroundColor: "#DCF1E4" },
  "&:focus": { backgroundColor: "#DCF1E4" },
};
const waItemSx = {
  fontFamily: FONT,
  fontSize: "13px",
  padding: "7px 10px",
  borderRadius: "8px",
  mx: 0.5,
  my: 0.3,
  "&:hover": { backgroundColor: "#F4F7FA" },
};

const INVENTORY_TABLE_COLUMNS = [
  { key: "type", label: "Type" },
  { key: "size", label: "Size" },
  { key: "status", label: "Status" },
  { key: "floor", label: "Floor" },
  { key: "totalFloor", label: "Total Floors" },
  { key: "parking", label: "Parking" },
  { key: "balcony", label: "Balcony" },
  { key: "bedroom", label: "Bedrooms" },
  { key: "bath", label: "Bathrooms" },
  {
    key: "possessionDate",
    label: "Possession Date",
    format: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
  { key: "price", label: "Price" },
  { key: "rent", label: "Rent" },
  { key: "age", label: "Age" },
  { key: "address", label: "Address" },
  // { key: "ownerName", label: "Owner Name" },
  // {
  //   key: "ownerContact",
  //   label: "Owner Contact",
  //   format: (value) => (value ? `+${value}` : "-"),
  // },
];

const ViewHistory = () => {
  const [lead, setLead] = useState();
  const [remarks, setRemarks] = useState([]);
  const [leadHistory, setLeadHistory] = useState();
  const { leadId } = useParams();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const isMobile = useMediaQuery("(max-width: 750px)");
  // const [role, setRole] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState();
  const [selectedLead, setSelectedLead] = useState(null);
  const [status, setStatus] = useState();
  // const decoded = jwtDecode(storedUser.accessToken);
  const { role } = useOrganization();
  const [formData, setFormData] = useState({
    nextFollowUp: "",
    status: "",
    remark: "",
  });
  const [ownedInventories, setOwnedInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const reminderTimeouts = useRef([]);
  const [template, setTemplate] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const [leadScore, setLeadScore] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const orgId = JSON.parse(localStorage.getItem("org"));

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgType, setMsgType] = useState("success");
  const showModal = (msg, type = "success") => {
    setMsgText(msg);
    setMsgType(type);
    setMsgOpen(true);
  };

  const fetchTemplate = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/template?projectId=${lead?.projectId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setTemplate(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchTemplate();
  }, [lead?.projectId]);

  const fetchLead = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${leadId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );

      setLead(res.data);
      setLeadScore(res.data?.leadScore || "");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/history?leadId=${leadId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );

      // Sort newest first
      const sorted = res.data.sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
      );

      setRemarks(sorted);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchOwnedInventories = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/inventory/project-and-phone?projectId=${lead?.projectId}&leadId=${leadId}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setOwnedInventories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch inventories", err);
    }
  };

  useEffect(() => {
    fetchLead();
    fetchData();
  }, [leadId]);

  useEffect(() => {
    fetchOwnedInventories();
  }, [lead?.projectId, leadId]);

  const fetchLeadByMobile = async (phone) => {
    if (!phone) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead/by-phone?mobile=${phone}&orgId=${orgId}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        },
      );
      setLeadHistory(res.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    const phone = lead?.phone || storedUser?.username;
    fetchLeadByMobile(lead?.phone);
  }, [lead?.phone]);

  const excludedKeys = new Set([
    "customFields",
    "assignedBy",
    "assignedTo",
    // "createdDate",
    "updatedDate",
    "status",
    "projectName",
    "isLead",
    "organization",
    "inventories",
    "comments",
  ]);

  function isDate(val) {
    if (typeof val !== "string") return false;

    // YYYY-MM-DD or full ISO timestamp
    return (
      /^\d{4}-\d{2}-\d{2}$/.test(val) ||
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)
    );
  }

  function toLabel(str) {
    return str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
  }

  // useEffect(() => {
  //   if (!storedUser?.accessToken) return;

  //   const role = decoded.userRole[0];
  //   setRole(role);
  // }, []);

  useEffect(() => {
    const fetchLeadStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${lead?.projectId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setStatus(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchLeadStatus();
  }, [lead]);

  const handleOpenModal = (lead) => {
    setSelectedLead(lead);
    setFormData({
      nextFollowUp: lead.nextFollowUp || "",
      status: lead.status || "",
      remark: "",
    });
    setOpenModal(true);
    setLeadScore(lead.leadScore || "");
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleUpdateLead = async () => {
    try {
      console.log(leadScore);
      setBtnLoading(true);
      if (formData.status || formData.nextFollowUp || formData.remark) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/lead/update-lead-status`,
          {
            username: storedUser.username,
            leadId: leadId,
            lastFollowUp: new Date().toISOString().split("T")[0],
            nextFollowUp: formData.nextFollowUp,
            remark: formData.remark,
            status: formData.status,
          },
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );
      } else if (leadScore && leadScore !== lead.leadScore) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${leadId}`,
          {
            orgId: orgId,
            leadScore: leadScore,
          },
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        fetchLead();
      } else {
        return;
      }

      showModal("Lead updated successfully", "success");
      setOpenModal(false);
      fetchData();
      setFormData({
        nextFollowUp: "",
        status: "",
        remark: "",
      });
    } catch (err) {
      console.log(err);
      showModal("Failed to update lead", "error");
    } finally {
      setBtnLoading(false);
    }
  };

  const handleWriteWithAI = async () => {
    try {
      setAiLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead/write-comment`,
        {
          leadName: lead?.name,
          requirement: lead?.requirement,
          preferredPropertyType: lead?.preferredPropertyType,
          status: formData.status || lead?.status,
          existingText: formData.remark || "",
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setFormData((prev) => ({ ...prev, remark: res.data }));
    } catch (error) {
      console.log(error);
      showModal(error?.response?.data?.Exception || "Failed to generate comment", "error");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAddComment = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/lead?leadId=${selectedLead.leadId}`,
        {
          reminder: comment,
        },
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );
      setOpenComment(false);

      const updatedLead = {
        ...selectedLead,
        reminder: comment,
      };

      const timeoutId = scheduleFollowUpReminder(updatedLead);

      if (timeoutId) {
        reminderTimeouts.current.push(timeoutId);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const sortedStatus = [...(status || [])].sort((a, b) => {
    const groupA = a.metaLeadStatus?.status || "";
    const groupB = b.metaLeadStatus?.status || "";
    if (groupA !== groupB) {
      return groupA.localeCompare(groupB);
    }
    return a.status.localeCompare(b.status);
  });

  function formatDate(date) {
    if (!date) return;
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const formattedDate = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} ${formattedTime}`;
  };

  const columns = [
    ...INVENTORY_TABLE_COLUMNS.map((col) => ({
      field: col.key,
      headerName: col.label,
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.row[col.key];

        if (col.format) {
          return col.format(value);
        }

        return value ?? "-";
      },
    })),
  ];

  const leadScoreOptions = [
    {
      label: "Excellent",
      options: [100, 90, 80],
    },
    {
      label: "Good",
      options: [70, 60, 50],
    },
    {
      label: "Warm",
      options: [50, 40, 30],
    },
    {
      label: "Cold",
      options: [20, 10, 0],
    },
  ].flatMap((group) =>
    group.options.map((score) => ({
      group: group.label,
      value: score,
    })),
  );

  // datetime-local adapter for the reminder modal: comment stays "YYYY-MM-DDTHH:mm"
  const reminderDate = (comment || "").slice(0, 10);
  const reminderTime = (comment || "").slice(11, 16);
  const setReminderPart = (d, t) => {
    if (!d && !t) return setComment("");
    setComment(`${d || new Date().toISOString().slice(0, 10)}T${t || "09:00"}`);
  };

  if (loading) {
    return (
      <ThemeProvider theme={viewTheme}>
        <Box>
          <Skeleton variant="rounded" height={500} sx={{ mt: 2, borderRadius: "14px" }} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!lead) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: 10,
          px: 3,
          fontFamily: FONT,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#FBEDEB",
            border: "1px solid #F2CFCA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 28, color: "#A6342A" }} />
        </Box>

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK, mb: 0.5 }}
        >
          You don't have access to this lead
        </Typography>

        <Typography
          variant="body2"
          sx={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.55, color: "#4A5A6E", maxWidth: 360, mb: 3 }}
        >
          This lead may belong to a different team or project you're not part
          of. Contact your admin if you think this is a mistake.
        </Typography>

        <Button
          variant="outlined"
          onClick={() => navigate("/dashboard")}
          sx={{ height: 40, px: 2.5, borderRadius: "10px", borderColor: FIELD_EDGE, color: INK, fontFamily: FONT, fontWeight: 600, textTransform: "none", "&:hover": { background: "#F4F7FA", borderColor: "#B4C6DA" } }}
        >
          Go back
        </Button>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={viewTheme}>
    <div style={{ fontFamily: FONT, color: INK, padding: "4px 0 24px" }}>
      {lead && (
        <>
          <div className="add-forms" style={cardStyle}>
            <Box
              sx={{
                display: { xs: "block", sm: "flex" },
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h2 style={{ ...h2Style, fontSize: 24 }}>Lead Information</h2>
                {role !== "VT1" && role !== "VT2" && role !== "PARTNER" && (
                  <Button
                    className="btn-contained"
                    onClick={() =>
                      navigate(
                        role === "ADMIN"
                          ? `/dashboard/edit-lead/${lead.leadId}`
                          : `/dashboard/edit-lead/${lead.leadId}`,
                      )
                    }
                    title="Edit lead"
                    sx={{
                      height: "fit-content",
                      margin: "0 !important",
                      minWidth: "0 !important",
                      width: 34,
                      minHeight: "34px !important",
                      p: "0 !important",
                      borderRadius: "10px !important",
                      background: "#FFFFFF !important",
                      color: `${INK_2} !important`,
                      border: `1px solid ${FIELD_EDGE} !important`,
                      "&:hover": { background: "#F4F7FA !important" },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 17 }} />
                  </Button>
                )}
              </Box>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: isMobile ? 10 : 0,
                  borderRadius: "999px",
                  background: "#EAF2FB",
                  border: "1px solid #C9DEF3",
                  color: "#1B4F82",
                  padding: "4px 14px",
                  height: "fit-content",
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Project: <strong style={{ fontWeight: 700 }}>{lead?.projectName}</strong>
              </span>
            </Box>
            <hr style={hrStyle} />
            <div>
              {/* -------- BASIC FIELDS -------- */}
              <Grid container spacing={2}>
                {Object.entries(lead)
                  .filter(
                    ([key, val]) =>
                      !excludedKeys.has(key) &&
                      val !== null &&
                      key !== "projectId" &&
                      key !== "leadScore" &&
                      key !== "aiScore" &&
                      key !== "partnerId" &&
                      key !== "remark" &&
                      key !== "ownedInventories" &&
                      key !== "countryCode" &&
                      key !== "alternateCountryCode" &&
                      key !== "isAdminAssigned" &&
                      key !== "isWhatsapp" &&
                      key !== "isAlternateWhatsapp",
                  )
                  .map(([key, val]) => (
                    <Grid size={{ xs: 12, sm: 3 }} key={key}>
                      <div>
                        <span style={factLabel}>
                          {toLabel(key)}:
                        </span>
                        {key === "partner" ? (
                          <span style={factValue}>
                            {`${val?.partnerName} (${val?.mobile})`}
                          </span>
                        ) : key === "phone" ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              fontSize: "14px",
                            }}
                          >
                            {(() => {
                              let cleaned = (lead.phone || "").replace(
                                /\D/g,
                                "",
                              );
                              let formatted =
                                lead.countryCode !== null
                                  ? lead.countryCode.includes("+")
                                    ? `${lead.countryCode}${cleaned}`
                                    : `+${lead.countryCode}${cleaned}`
                                  : `+${cleaned}`;

                              return (
                                <a
                                  href={`tel:${formatted}`}
                                  style={{
                                    color: PRIMARY,
                                    fontFamily: MONO,
                                    fontWeight: 600,
                                    fontSize: 13.5,
                                    textDecoration: "underline",
                                    textUnderlineOffset: 3,
                                  }}
                                >
                                  {formatted}
                                </a>
                              );
                            })()}

                            {role !== "PARTNER" && (
                              <IconButton
                                size="small"
                                onClick={handleOpen}
                                sx={waBtnSx}
                              >
                                <WhatsAppIcon sx={{ width: 18, height: 18 }} />
                                <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.25 }} />
                              </IconButton>
                            )}
                            <Menu
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl)}
                              onClose={handleClose}
                              slotProps={{
                                paper: {
                                  sx: {
                                    boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
                                    border: `1px solid ${LINE}`,
                                    borderRadius: "12px",
                                    mt: 1,
                                    minWidth: 200,
                                  },
                                },
                              }}
                            >
                              {template?.length === 0 ? (
                                <MenuItem sx={{ ...waItemSx, color: MUTED }}>
                                  No templates added
                                </MenuItem>
                              ) : (
                                template?.map((template) => (
                                  <MenuItem
                                    key={template.templateId}
                                    onClick={() => {
                                      let cleaned = lead.phone.replace(
                                        /\D/g,
                                        "",
                                      );
                                      let formatted =
                                        lead.countryCode !== null
                                          ? lead.countryCode.includes("+")
                                            ? `${lead.countryCode.replace("+", "")}${cleaned}`
                                            : `${lead.countryCode}${cleaned}`
                                          : `${cleaned}`;
                                      const parameters = {
                                        name: lead.name || "",
                                        projectName: lead?.projectName || "",
                                        senderName:
                                          JSON.parse(
                                            localStorage.getItem("name"),
                                          ) || "",
                                      };

                                      const personalizedMessage =
                                        template.template.replace(
                                          /{{\s*(\w+)\s*}}/g,
                                          (match, key) =>
                                            parameters[key] ?? match,
                                        );
                                      const whatsappUrl = `https://api.whatsapp.com/send?phone=${formatted}&text=${encodeURIComponent(
                                        personalizedMessage,
                                      )}`;
                                      window.open(whatsappUrl, "_blank");
                                      handleClose();
                                    }}
                                    sx={waItemSx}
                                  >
                                    {template.title}
                                  </MenuItem>
                                ))
                              )}
                              <MenuItem
                                onClick={() => {
                                  let cleaned = lead.phone.replace(/\D/g, "");
                                  let formatted =
                                    lead.countryCode !== null
                                      ? lead.countryCode.includes("+")
                                        ? `${lead.countryCode.replace("+", "")}${cleaned}`
                                        : `${lead.countryCode}${cleaned}`
                                      : `${cleaned}`;
                                  const whatsappUrl = `https://api.whatsapp.com/send?phone=${formatted}`;
                                  window.open(whatsappUrl, "_blank");
                                  handleClose();
                                }}
                                sx={{
                                  ...waItemSx,
                                  fontWeight: 700,
                                  color: WA,
                                  borderTop:
                                    template?.length > 0
                                      ? "1px solid #EDF1F5"
                                      : "none",
                                }}
                              >
                                Custom Message
                              </MenuItem>
                            </Menu>
                          </span>
                        ) : key === "alternatePhone" ? (
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              fontSize: "14px",
                            }}
                          >
                            {(() => {
                              let cleaned = (lead.alternatePhone || "").replace(
                                /\D/g,
                                "",
                              );
                              let formatted =
                                lead.alternateCountryCode !== null
                                  ? lead.alternateCountryCode.includes("+")
                                    ? `${lead.alternateCountryCode}${cleaned}`
                                    : `+${lead.alternateCountryCode}${cleaned}`
                                  : `+${cleaned}`;

                              return (
                                <a
                                  href={`tel:${formatted}`}
                                  style={{
                                    color: PRIMARY,
                                    fontFamily: MONO,
                                    fontWeight: 600,
                                    fontSize: 13.5,
                                    textDecoration: "underline",
                                    textUnderlineOffset: 3,
                                  }}
                                >
                                  {formatted}
                                </a>
                              );
                            })()}
                          </span>
                        ) : (
                          <span
                            style={{
                              ...factValue,
                              fontFamily: isDate(val) || typeof val === "number" ? MONO : FONT,
                              fontSize: isDate(val) || typeof val === "number" ? 13.5 : 14,
                            }}
                          >
                            {isDate(val)
                              ? key !== "createdDate"
                                ? formatDate(val)
                                : formatDateTime(val)
                              : String(val)}
                          </span>
                        )}
                      </div>
                    </Grid>
                  ))}
              </Grid>
              {/* -------- CUSTOM FIELDS -------- */}
              {lead.customFields?.length > 0 && (
                <>
                  <hr style={hrStyle} />
                  <h3 style={{ ...sectionLabel, margin: "0 0 12px" }}>Custom Fields</h3>

                  <Grid container spacing={2}>
                    {lead.customFields.map((cf) => (
                      <Grid size={{ xs: 12, sm: 3 }} key={cf.fieldId}>
                        <div>
                          <span style={factLabel}>
                            {cf.label}:
                          </span>{" "}
                          {cf.value !== null && (
                            <span style={factValue}>
                              {isDate(cf.value)
                                ? formatDate(cf.value)
                                : String(cf.value)}
                            </span>
                          )}
                          {cf.selectedOption && (
                            <span style={factValue}>{cf.selectedOption}</span>
                          )}
                          {cf.selectedOptions && (
                            <span style={factValue}>{cf.selectedOptions.join(", ")}</span>
                          )}
                        </div>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
              <hr style={hrStyle} />
              <Grid container spacing={2}>
                {Object.entries(lead)
                  .filter(
                    ([key, val]) =>
                      !excludedKeys.has(key) &&
                      val !== null &&
                      typeof val !== "object" &&
                      key === "leadScore",
                  )
                  .map(([key, val]) => (
                    <Grid size={{ xs: 12, sm: 3 }} key={key}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 14px",
                          background: "#F7F9FC",
                          border: `1px solid ${LINE}`,
                          borderRadius: "12px",
                        }}
                      >
                        <span style={{ ...factLabel, marginBottom: 0 }}>
                          {toLabel(key)}:
                        </span>
                        <span
                          style={{
                            fontFamily: MONO,
                            color: PRIMARY,
                            fontWeight: 600,
                            fontSize: "24px",
                            letterSpacing: "-0.03em",
                            marginLeft: "20px",
                          }}
                        >
                          {isDate(val) ? formatDate(val) : String(val)}
                        </span>
                      </div>
                    </Grid>
                  ))}
              </Grid>
            </div>
          </div>
        </>
      )}
      {role !== "PARTNER" && (
        <Button
          className="btn-contained"
          onClick={() => {
            localStorage.setItem(
              "leadData",
              JSON.stringify({
                name: lead.name,
                phone: `${lead.countryCode !== null ? (lead.countryCode.includes("+") ? lead.countryCode.substring(1) : lead.countryCode) : ""}${lead.phone}`,
                leadId: lead.leadId,
              }),
            );
            navigate(`/dashboard/inventory/add-inventory`);
          }}
          sx={{
            width: { xs: "100%", sm: "auto" },
            margin: "0 0 16px !important",
            height: 38,
            px: 2,
            borderRadius: "10px !important",
            background: "#FFFFFF !important",
            color: `${INK} !important`,
            border: `1px solid ${FIELD_EDGE} !important`,
            "&:hover": { background: "#F4F7FA !important" },
          }}
        >
          Add Inventory
        </Button>
      )}
      {ownedInventories.length > 0 && (
        <div className="add-forms" style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #EDF1F5" }}>
            <h2 style={{ ...h2Style, fontSize: 16 }}>Owned Inventories Details</h2>
          </div>
          <Box sx={{ background: "#fff" }}>
            <DataGrid
              rows={ownedInventories}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 20,
                  },
                },
              }}
              pageSizeOptions={[20]}
              disableRowSelectionOnClick
              disableColumnSelector
              disableDensitySelector
              // slots={{ toolbar: CustomToolbar }}
              getRowId={(row) => row.inventoryId}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
              }
              rowHeight={48}
              columnHeaderHeight={40}
              sx={{
                position: "relative",
                border: "none",
                fontFamily: `${FONT} !important`,
                color: INK,

                "& .MuiDataGrid-columnHeaderTitle": {
                  fontFamily: FONT,
                  fontWeight: "700 !important",
                  fontSize: 10.5,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: MUTED,
                },
                "& .MuiDataGrid-columnHeader, & .MuiDataGrid-filler": {
                  background: "#F7F9FC !important",
                },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: `1px solid ${LINE}`,
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#F4F7FA",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "13.5px",
                  color: INK_2,
                  border: "none",
                  borderBottom: "1px solid #F1F4F8",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnSeparator": {
                  display: "none",
                },
                "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${LINE}` },
                "& .MuiTablePagination-root, & .MuiTablePagination-displayedRows": {
                  fontFamily: MONO,
                  fontSize: 12,
                  color: MUTED,
                },
                [`& .${gridClasses.row}.even`]: {
                  backgroundColor: "#FFFFFF",
                  // '&:hover': {
                  //   backgroundColor: "#f6f6f6",
                  // },
                },
                [`& .${gridClasses.row}.odd`]: {
                  backgroundColor: "#FCFDFE",
                },
              }}
            />
          </Box>
        </div>
      )}
      {remarks.length > 0 && (
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #EDF1F5", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <h2 style={{ ...h2Style, fontSize: 16, marginBottom: 0, paddingInline: 0 }}>
              Follow-up History
            </h2>
            <span style={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{remarks.length}</span>
          </div>
          {isMobile ? (
            remarks.map((r, idx) => (
              <div
                key={idx}
                className="card"
                style={{
                  margin: 0,
                  padding: "12px 16px",
                  background: "transparent",
                  border: 0,
                  borderBottom: "1px solid #F1F4F8",
                  borderRadius: 0,
                  boxShadow: "none",
                }}
              >
                <Typography fontSize={"14px"} fontWeight={700} mb={1} sx={{ fontFamily: FONT, color: INK }}>
                  {r.commentBy === "ADMIN"
                    ? r?.organization?.adminName || "Admin"
                    : r.commentBy === ""
                      ? "System Comment"
                      : r.orgUser?.name || ""}
                  <br />
                  <span style={{ fontFamily: MONO, fontSize: "11.5px", fontWeight: 500, color: MUTED }}>
                    {r.lastFollowUp === null
                      ? "-"
                      : formatDateTime(r?.createdDate)}
                  </span>
                </Typography>
                <Typography
                  fontSize={"14px"}
                  fontWeight={500}
                  mb={1}
                  sx={{ display: "flex", alignItems: "center", gap: 2, fontFamily: FONT, color: INK, lineHeight: 1.5 }}
                >
                  {r.remark}
                </Typography>
                <Typography
                  fontSize={"12.5px"}
                  fontWeight={500}
                  mb={0.5}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT, color: MUTED }}
                >
                  Next Follow-up:{" "}
                  <span style={{ fontFamily: MONO, fontWeight: 600, color: INK_2 }}>
                    {r.nextFollowUp === null ? "-" : formatDate(r?.nextFollowUp)}
                  </span>
                </Typography>
                <Typography
                  fontSize={"12.5px"}
                  fontWeight={500}
                  mb={0.5}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT, color: MUTED }}
                >
                  Status:{" "}
                  <span style={{ padding: "1px 9px", borderRadius: 999, background: "#EAF2FB", border: "1px solid #C9DEF3", color: "#1B4F82", fontWeight: 600 }}>{r.status}</span>
                </Typography>
              </div>
            ))
          ) : (
            <div
              style={{
                overflowX: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: FONT,
                  fontSize: "13.5px",
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle}>Comment</th>
                    <th style={thStyle}>Comment By</th>
                    <th style={thStyle}>Last Follow-up</th>
                    <th style={thStyle}>Next Follow-up</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {remarks.length > 0 ? (
                    remarks.map((r, idx) => (
                      <tr
                        key={idx}
                        style={{
                          background: idx % 2 ? "#FCFDFE" : "#FFFFFF",
                          borderBottom: "1px solid #F1F4F8",
                        }}
                      >
                        <td style={{ ...tdStyle, color: INK, fontWeight: 500, lineHeight: 1.5, maxWidth: 420 }}>{r.remark}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                          {r.commentBy === "ADMIN"
                            ? r?.organization?.adminName || "Admin"
                            : r.commentBy === "webhook"
                              ? "System Comment"
                              : r.orgUser?.name || ""}
                        </td>
                        <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 12.5, whiteSpace: "nowrap" }}>
                          {r.lastFollowUp === null
                            ? "-"
                            : formatDateTime(r?.createdDate)}
                        </td>
                        <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 12.5, whiteSpace: "nowrap" }}>
                          {r.nextFollowUp === null
                            ? "-"
                            : formatDate(r?.nextFollowUp)}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, background: "#EAF2FB", border: "1px solid #C9DEF3", color: "#1B4F82", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        style={{ textAlign: "center", padding: "16px", color: MUTED }}
                      >
                        No history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {leadHistory?.length > 0 && (
        <div>
          {leadHistory
            ?.filter((history) => history.projectName !== lead.projectName)
            .map((lead) => (
              <Grid container spacing={2}>
                <Grid
                  item
                  size={{ xs: 12, sm: 4 }}
                  sx={{
                    background: "#FFFFFF",
                    border: `1px solid ${LINE}`,
                    borderRadius: "12px",
                    px: 1.75,
                    py: 0.5,
                    my: 1,
                    fontFamily: FONT,
                    fontSize: 13.5,
                    color: INK_2,
                    "& p": { margin: "6px 0" },
                  }}
                >
                  <p>Lead ID: <span style={{ fontFamily: MONO }}>{lead.leadId}</span></p>
                  <p>Name: <strong style={{ color: INK }}>{lead.name}</strong></p>
                  <p>Email: {lead.email}</p>
                  <p>Project Name: {lead.projectName}</p>
                </Grid>
              </Grid>
            ))}
        </div>
      )}
      {role !== "VT1" && role !== "VT2" && role !== "PARTNER" && (
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "14px",
            boxShadow: "none",
            p: isMobile ? 2 : 2.5,
            mb: 2,
          }}
        >
          <div style={{ ...sectionLabel, marginBottom: 12 }}>Log a follow-up</div>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <label htmlFor="" style={labelStyle}>Next Follow Up Date</label>
              <ThemedDatePicker
                name="nextFollowUp"
                value={formData.nextFollowUp}
                onChange={(e) =>
                  setFormData({ ...formData, nextFollowUp: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <label htmlFor="" style={labelStyle}>Lead Status</label>
              <Autocomplete
                name="status"
                options={sortedStatus || []}
                getOptionLabel={(option) => option.status || ""}
                groupBy={(option) => option.metaLeadStatus.status}
                value={
                  status?.find((p) => p.status === formData.status) || null
                }
                onChange={(e, value) => {
                  setFormData({ ...formData, status: value?.status || "" });
                }}
                isOptionEqualToValue={(option, value) =>
                  option.status === value.status
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Status" />
                )}
                sx={{
                  margin: 0,
                  "& .MuiInputBase-root": {
                    minHeight: 42,
                    fontSize: "14px",
                    borderRadius: "10px",
                    padding: "1px 7px",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <label htmlFor="" style={labelStyle}>Lead Score</label>
              <Autocomplete
                options={leadScoreOptions}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.value.toString()}
                value={
                  leadScoreOptions?.find((opt) => opt.value === leadScore) ||
                  null
                }
                onChange={(event, newValue) => {
                  setLeadScore(newValue?.value || null);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Lead Score" />
                )}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: 42,
                    padding: "1px 7px",
                  },
                  "& input::placeholder": {
                    color: "#8A97A8 !important",
                    opacity: 1,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <label htmlFor="" style={{ ...labelStyle, margin: 0 }}>Comment</label>
                <Button
                  size="small"
                  startIcon={<AutoFixHighIcon fontSize="small" />}
                  onClick={handleWriteWithAI}
                  disabled={aiLoading}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    minWidth: 0,
                    color: "#4A3FBF",
                    background: "#EFEDFB",
                    border: "1px solid #D3D0F2",
                    "&:hover": { background: "#E7E4F8" },
                  }}
                >
                  {aiLoading ? "Writing..." : "Write with AI"}
                </Button>
              </Box>{" "}
              <textarea
                name="remark"
                rows={4}
                value={formData.remark}
                onChange={(e) =>
                  setFormData({ ...formData, remark: e.target.value })
                }
                placeholder="Enter comment..."
                style={{
                  display: "block",
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "10px 12px",
                  margin: 0,
                  borderRadius: "10px",
                  border: `1px solid ${FIELD_EDGE}`,
                  background: "#FFFFFF",
                  resize: "vertical",
                  fontFamily: FONT,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: INK,
                  outline: "none",
                }}
              />
            </Grid>
          </Grid>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button
              className="btn-contained"
              variant="contained"
              sx={{
                mt: 2,
                height: 42,
                px: 3,
                width: { xs: "100%", sm: "auto" },
                background: `${PRIMARY} !important`,
                color: "#FFFFFF !important",
                border: "0 !important",
                borderRadius: "10px !important",
                "&:hover": { background: "#084767 !important" },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
              onClick={handleUpdateLead}
              disabled={btnLoading}
            >
              {btnLoading ? "Updating..." : "Comment"}
            </Button>
          </div>
        </Box>
      )}

      {lead && role !== "PARTNER" && (
        <div className="add-forms" style={cardStyle}>
          <h2 style={{ ...h2Style, fontSize: 16 }}>Assignment Details</h2>
          <hr style={hrStyle} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <div>
              <span style={factLabel}>
                Assigned By:
              </span>
              <span style={factValue}>
                {lead.assignedBy !== null
                  ? `${lead.assignedBy?.name} (${lead.assignedBy?.email})`
                  : `${lead?.organization?.adminName || "Admin"} (${lead?.organization?.adminEmail})`}
              </span>
            </div>

            <div>
              <span style={factLabel}>
                Assigned To:
              </span>
              <span style={factValue}>
                {(() => {
                  const orgUserNames = lead?.assignedTo?.map((u) => u.name) || [];
                  const adminName = lead?.organization?.adminName || "Admin";

                  const allNames = lead?.isAdminAssigned
                    ? [adminName, ...orgUserNames]
                    : orgUserNames;

                  return allNames.length > 0
                    ? allNames.join(", ")
                    : `${adminName} (${lead?.organization?.adminEmail})`;
                })()}
              </span>
            </div>
          </Box>
        </div>
      )}

      <Modal open={openComment} onClose={() => setOpenComment(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "90%" : 440,
            bgcolor: "#FFFFFF",
            border: 0,
            borderRadius: "18px",
            boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
            p: isMobile ? 2 : 3,
            outline: "none",
            fontFamily: FONT,
          }}
        >
          <h3 style={{ margin: "0 0 14px", fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK }}>
            Set Reminder for Lead <span style={{ fontFamily: MONO, fontWeight: 600 }}>{selectedLead?.leadId}</span>
          </h3>

          <label htmlFor="" style={labelStyle}>Select Date Time</label>
          <Box sx={{ display: "flex", gap: 1 }}>
            <ThemedDatePicker
              name="reminder"
              value={reminderDate}
              onChange={(e) => setReminderPart(e.target.value, reminderTime)}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <ThemedTimePicker
              value={reminderTime}
              onChange={(e) => setReminderPart(reminderDate, e.target.value)}
              step={300}
              height={42}
              width={140}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2.5, height: 42 }}
            onClick={handleAddComment}
          >
            Submit
          </Button>
        </Box>
      </Modal>

      <ErrorModal
        open={msgOpen}
        handleClose={() => setMsgOpen(false)}
        msg={msgText}
        type={msgType}
      />
    </div>
    </ThemeProvider>
  );
};

export default ViewHistory;

const thStyle = {
  padding: "10px 16px",
  textAlign: "left",
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  color: MUTED,
  background: "#F7F9FC",
  borderBottom: `1px solid ${LINE}`,
};

const tdStyle = {
  padding: "12px 16px",
  verticalAlign: "top",
  color: INK_2,
};

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN");
}